import {
  ReactNode,
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  MutableRefObject,
} from 'react';
import noop from '@sdk/utils/noop';

type BillingViewContextType = {
  isBillingInformationEditMode: boolean;
  setIsBillingInformationEditMode: (isEditMode: boolean) => void;
  billingInfoContainerRef: MutableRefObject<HTMLDivElement>;
  scrollToBillingInfoEdit: VoidFunction;
  isSubmitted: boolean;
  setIsSubmitted: (isSubmitted: boolean) => void;
  shouldShowAddressErrors: boolean;
};

const BillingViewContext = createContext<BillingViewContextType>({
  isBillingInformationEditMode: false,
  setIsBillingInformationEditMode: noop,
  billingInfoContainerRef: null,
  scrollToBillingInfoEdit: noop,
  isSubmitted: false,
  setIsSubmitted: noop,
  shouldShowAddressErrors: false,
});

type Props = {
  children: ReactNode;
  isEditBillingInformationActive: boolean;
  shouldShowAddressErrors: boolean;
};

const BillingViewProvider = ({
  children,
  isEditBillingInformationActive,
  shouldShowAddressErrors,
}: Props) => {
  const [isBillingInformationEditMode, setIsBillingInformationEdit] = useState(
    isEditBillingInformationActive
  );
  const [isSubmitted, setIsSubmitted] = useState(false);
  const billingInfoContainerRef = useRef<HTMLDivElement>(null);
  const scrollToBillingInfoEdit = useCallback(() => {
    if (billingInfoContainerRef.current) {
      billingInfoContainerRef.current.scrollIntoView();
    }
  }, []);

  const value: BillingViewContextType = {
    isBillingInformationEditMode,
    setIsBillingInformationEditMode: setIsBillingInformationEdit,
    billingInfoContainerRef,
    scrollToBillingInfoEdit,
    isSubmitted,
    setIsSubmitted,
    shouldShowAddressErrors,
  };

  return <BillingViewContext.Provider value={value}>{children}</BillingViewContext.Provider>;
};

function useBillingView(): BillingViewContextType {
  const context = useContext(BillingViewContext);
  if (!context) {
    throw new Error('BillingViewContext must be used within a BillingViewProvider');
  }

  return context;
}

export { BillingViewProvider, useBillingView };
